import React, { useState, useEffect, useCallback } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import CircularProgress from "@material-ui/core/CircularProgress";
import RateReviewOutlined from "@material-ui/icons/RateReviewOutlined";
import ErrorOutlineOutlined from "@material-ui/icons/ErrorOutlineOutlined";
import SchoolOutlined from "@material-ui/icons/SchoolOutlined";
import ThumbUpAltOutlined from "@material-ui/icons/ThumbUpAltOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import useMenuStyles from "../../components/menuStyles";
import DiffView, { normWs } from "../../components/DiffView";

// Fila de CORREÇÕES do agente (handoff §3): o supervisor, no Numera Atende, revisa
// um atendimento da IA e corrige. Clicar numa correção abre a REVISÃO COMPLETA num
// modal — já com a proposta da IA (o diff "muda disso pra isso") pronta para
// aprovar. Nada é aplicado sozinho (§3.5); aprovar publica a nova versão (§4).
const KIND_LABEL = {
  resposta_errada: "Resposta errada",
  resposta_correta: "Resposta certa (reforço)",
  conhecimento: "Faltou conhecimento",
};
const KIND_ICON = {
  resposta_errada: ErrorOutlineOutlined,
  resposta_correta: ThumbUpAltOutlined,
  conhecimento: SchoolOutlined,
};
const SCOPE_LABEL = { geral: "vale pra sempre", conversa: "só nesta conversa" };
const STATUSES = [
  { key: "pending", label: "Pendentes" },
  { key: "approved", label: "Aprovadas" },
  { key: "rejected", label: "Recusadas" },
  { key: "", label: "Todas" },
];

const selectMenuProps = menuCls => ({
  classes: { paper: menuCls.selectPaper },
  getContentAnchorEl: null,
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
});

const useStyles = makeStyles(theme => {
  const t = k => (theme.app ? theme.app[k] : undefined);
  return {
    wrap: { padding: "20px 0" },
    header: { display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 18 },
    desc: { flex: 1, fontSize: 13.5, color: t("textTertiary") || "#929AA4", lineHeight: 1.5 },
    filter: { flexShrink: 0, minWidth: 160 },
    // vazio — mesmo card das demais telas (AgentEventos)
    empty: {
      background: "#fff", border: `1px solid ${t("border") || "#E6E9ED"}`, borderRadius: 16,
      padding: theme.spacing(7, 3), textAlign: "center",
    },
    emptyIcon: {
      width: 46, height: 46, borderRadius: 999, background: t("lineSoft") || "#F1F4F2",
      display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12,
      "& svg": { fontSize: 22, color: t("textTertiary") || "#929AA4" },
    },
    emptyText: { color: t("textSecondary") || "#5B6470", fontSize: 13.5 },
    // lista — cards iguais aos das demais telas
    list: { display: "flex", flexDirection: "column", gap: 10 },
    card: {
      display: "flex", alignItems: "center", gap: 14, background: "#fff", cursor: "pointer",
      border: `1px solid ${t("border") || "#E6E9ED"}`, borderRadius: 16, padding: "14px 18px",
      transition: "border-color .12s, box-shadow .12s",
      "&:hover": { borderColor: t("borderInput") || "#cfd6dd", boxShadow: "0 2px 12px rgba(27,34,42,.06)" },
    },
    cardIcon: {
      width: 38, height: 38, flexShrink: 0, borderRadius: 999, background: t("lineSoft") || "#F1F4F2",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      "& svg": { fontSize: 20, color: t("textTertiary") || "#929AA4" },
    },
    cardMain: { flex: 1, minWidth: 0 },
    phrase: { fontSize: 14, color: t("ink") || "#2c3036", lineHeight: 1.45, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    sub: { fontSize: 12.5, color: t("textTertiary") || "#929AA4", marginTop: 3 },
    badge: { fontSize: 11, fontWeight: 700, color: "#5B6470", background: "#EEF1F4", borderRadius: 6, padding: "2px 7px", flexShrink: 0 },
    badgeOk: { color: "#1E7A49", background: "#DCF5E7" },
    badgeBad: { color: "#D14343", background: "#FBE3E3" },
    // modal — mesmas peças do AgentVersoes (diff) + AgentEventos (campos)
    flabel: { fontSize: 11.5, fontWeight: 700, color: t("textTertiary") || "#929AA4", textTransform: "uppercase", letterSpacing: 0.3, margin: "14px 0 3px" },
    flabelFirst: { marginTop: 2 },
    orig: { fontSize: 13.5, color: t("textSecondary") || "#5B6470", fontStyle: "italic", background: "#F6F7F9", borderRadius: 8, padding: "8px 12px" },
    field: { fontSize: 14, color: t("ink") || "#2c3036", lineHeight: 1.45 },
    meta: { fontSize: 12.5, color: t("textTertiary") || "#929AA4", marginTop: 12 },
    secTitle: { fontSize: 13, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: "#5B6470", margin: "20px 0 10px" },
    loading: { display: "flex", alignItems: "center", gap: 10, color: t("textSecondary") || "#5B6470", fontSize: 13.5, padding: "10px 0" },
    emptyInline: { fontSize: 14, color: t("textSecondary") || "#5B6470", padding: "8px 0" },
    diffSec: { margin: "10px 0 16px" },
    diffHead: { display: "flex", alignItems: "center", gap: 8, marginBottom: 6 },
    diffLabel: { fontSize: 12.5, fontWeight: 700, color: "#5B6470" },
    editLink: { marginLeft: "auto", fontSize: 12.5, fontWeight: 600, color: t("green") || "#2e7d32", cursor: "pointer", userSelect: "none" },
    editBox: { marginTop: 8 },
    summary: { fontSize: 14, fontWeight: 600, color: t("ink") || "#2c3036", margin: "0 0 10px" },
    staleBox: { display: "flex", alignItems: "center", gap: 12, background: "#FFF8E1", border: "1px solid #F1E2B3", color: "#7A5A00", borderRadius: 10, padding: "10px 14px", fontSize: 13, lineHeight: 1.45 },
    analyzeBtn: { flexShrink: 0, textTransform: "none", fontSize: 12.5, fontWeight: 600, color: "#fff", background: t("dark") || "#1B222A", borderRadius: 8, padding: "5px 14px", boxShadow: "none", "&:hover": { background: t("ink") || "#0F141A", boxShadow: "none" } },
  };
});

const AgentCorrecoes = ({ agentId }) => {
  const classes = useStyles();
  const menu = useMenuStyles();
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState(0);
  const [status, setStatus] = useState("pending");

  const [open, setOpen] = useState(null); // a correção aberta no modal
  const [proposal, setProposal] = useState(null); // proposta da IA (diff)
  const [loadingProp, setLoadingProp] = useState(false);
  const [edit, setEdit] = useState({}); // {key: afterText}
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState({}); // {key: true} seções com o editor aberto

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/agents/${agentId}/corrections`, { params: status ? { status } : {} });
      setRows(data.corrections || []);
      setPending(data.pending || 0);
    } catch (err) {
      toastError(err);
    }
  }, [agentId, status]);

  useEffect(() => {
    load();
  }, [load]);

  const openReview = async c => {
    setOpen(c);
    setProposal(null);
    setEdit({});
    setEditing({});
    if (c.status !== "pending") return; // histórico: só mostra a correção
    setLoadingProp(true);
    try {
      // gera/retorna a proposta da IA (o diff) desta correção.
      const { data } = await api.post(`/corrections/${c.id}/proposal`);
      setProposal(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingProp(false);
    }
  };

  const close = () => {
    if (busy) return;
    setOpen(null);
    setProposal(null);
    setEdit({});
  };

  const patchOf = () => {
    if (!proposal) return [];
    return (proposal.patch || []).map(it => ({ ...it, after: edit[it.key] !== undefined ? edit[it.key] : it.after }));
  };
  const isEdited = () => proposal && (proposal.patch || []).some(it => edit[it.key] !== undefined && edit[it.key] !== it.after);
  // a proposta muda algum CONTEÚDO? (só espaço/linha em branco não conta)
  const hasContent = () => patchOf().some(it => normWs(it.after) !== normWs(it.before));

  // base mudou (outra versão foi publicada depois da proposta): re-ancora aqui mesmo
  const rebase = async () => {
    if (!proposal) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/proposals/${proposal.id}`, { action: "rebase" });
      setProposal(data);
      setEdit({});
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  // APROVAR = aprovar a proposta da IA → aplica o patch e publica a nova versão
  // (e marca a correção como aprovada). É aqui que "sabendo o que a IA vai mudar".
  const approve = async () => {
    if (!proposal) return;
    setBusy(true);
    try {
      const body = { action: "approve" };
      if (isEdited()) body.patch = patchOf();
      await api.patch(`/proposals/${proposal.id}`, body);
      toast.success("Correção aprovada — nova versão publicada.");
      close();
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const setCorrStatus = async newStatus => {
    if (!open) return;
    setBusy(true);
    try {
      await api.patch(`/corrections/${open.id}`, { status: newStatus });
      // recusar a correção descarta também a proposta da IA ligada a ela
      if (newStatus === "rejected" && proposal) await api.patch(`/proposals/${proposal.id}`, { action: "reject" }).catch(() => {});
      toast.success(newStatus === "rejected" ? "Correção recusada." : "Reaberta.");
      close();
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const snippet = c => c.why || c.expected || c.messageText || "(sem descrição)";

  return (
    <div className={classes.wrap}>
      <div className={classes.header}>
        <div className={classes.desc}>
          Correções são solicitadas por um supervisor humano em algum momento da jornada. Ele diz o que precisa mudar e
          a IA sugere a correção no treinamento atual.
        </div>
        <TextField
          className={classes.filter}
          select
          size="small"
          variant="outlined"
          label="Mostrar"
          value={status}
          onChange={e => setStatus(e.target.value)}
          SelectProps={{ MenuProps: selectMenuProps(menu) }}
        >
          {STATUSES.map(s => (
            <MenuItem key={s.key || "all"} value={s.key}>
              {s.label}{s.key === "pending" && pending > 0 ? ` (${pending})` : ""}
            </MenuItem>
          ))}
        </TextField>
      </div>

      {rows.length === 0 ? (
        <div className={classes.empty}>
          <div className={classes.emptyIcon}><RateReviewOutlined /></div>
          <div className={classes.emptyText}>Nenhuma correção {status === "pending" ? "pendente" : ""} por aqui.</div>
        </div>
      ) : (
        <div className={classes.list}>
          {rows.map(c => {
            const Icon = KIND_ICON[c.kind] || RateReviewOutlined;
            return (
              <div key={c.id} className={classes.card} onClick={() => openReview(c)}>
                <span className={classes.cardIcon}><Icon /></span>
                <div className={classes.cardMain}>
                  <div className={classes.phrase}>{snippet(c)}</div>
                  <div className={classes.sub}>
                    {KIND_LABEL[c.kind] || c.kind}
                    {c.by && c.by.name ? ` · ${c.by.name}` : ""}
                    {c.at ? ` · ${new Date(c.at).toLocaleDateString("pt-BR")}` : ""}
                  </div>
                </div>
                {c.status === "approved" ? <span className={`${classes.badge} ${classes.badgeOk}`}>APROVADA</span> : null}
                {c.status === "rejected" ? <span className={`${classes.badge} ${classes.badgeBad}`}>RECUSADA</span> : null}
                {c.status === "pending" ? (
                  <Button className={classes.analyzeBtn} onClick={e => { e.stopPropagation(); openReview(c); }}>Analisar</Button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!open} onClose={close} maxWidth="md" fullWidth PaperProps={{ className: menu.dialogPaper }}>
        <DialogTitle>{open && open.status === "pending" ? "Analisar sugestão" : "Correção"}</DialogTitle>
        <DialogContent dividers>
          {open ? (
            <>
              {/* a correção do supervisor */}
              {open.messageText ? (
                <div>
                  <div className={`${classes.flabel} ${classes.flabelFirst}`}>A IA disse</div>
                  <div className={classes.orig}>{open.messageText}</div>
                </div>
              ) : null}
              {open.why ? (
                <div><div className={classes.flabel}>Problema apontado</div><div className={classes.field}>{open.why}</div></div>
              ) : null}
              {open.expected ? (
                <div><div className={classes.flabel}>Deveria ser</div><div className={classes.field}>{open.expected}</div></div>
              ) : null}
              <div className={classes.meta}>
                {(open.by && open.by.name) || "—"}{open.by && open.by.role ? ` · ${open.by.role}` : ""}
                {open.scope ? ` · ${SCOPE_LABEL[open.scope] || open.scope}` : ""}
              </div>

              {/* a proposta da IA (o diff) */}
              {open.status === "pending" && (
                <>
                  <div className={classes.secTitle}>O que a IA quer mudar no agente</div>
                  {loadingProp ? (
                    <div className={classes.loading}><CircularProgress size={18} /> A IA está escrevendo a proposta de ajuste…</div>
                  ) : !proposal ? (
                    <div className={classes.emptyInline}>Não foi possível gerar a proposta.</div>
                  ) : proposal.stale ? (
                    <div className={classes.staleBox}>
                      <span style={{ flex: 1 }}>O agente mudou desde que esta sugestão foi criada. Atualize para comparar com a versão atual.</span>
                      <Button className={classes.analyzeBtn} disabled={busy} onClick={rebase}>Atualizar</Button>
                    </div>
                  ) : (proposal.patch || []).length === 0 ? (
                    <div className={classes.emptyInline}>A IA não gerou uma mudança automática. Recuse, ou ajuste a persona do agente à mão.</div>
                  ) : (
                    <>
                      {proposal.summary ? <div className={classes.summary}>{proposal.summary}</div> : null}
                      {patchOf().map(it => (
                        <div key={it.key} className={classes.diffSec}>
                          <div className={classes.diffHead}>
                            <span className={classes.diffLabel}>{it.label}</span>
                            <span className={classes.editLink} onClick={() => setEditing(e => ({ ...e, [it.key]: !e[it.key] }))}>
                              {editing[it.key] ? "Fechar ajuste" : "Ajustar texto"}
                            </span>
                          </div>
                          <DiffView before={it.before} after={it.after} />
                          {editing[it.key] && (
                            <div className={classes.editBox}>
                              <TextField multiline minRows={4} fullWidth variant="outlined" size="small"
                                label="Texto novo (o comparativo acima acompanha)"
                                value={it.after} onChange={e => setEdit(prev => ({ ...prev, [it.key]: e.target.value }))} />
                            </div>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          {open && open.status === "pending" ? (
            <>
              <Button onClick={() => setCorrStatus("rejected")} disabled={busy}>Recusar</Button>
              <Button onClick={approve} color="primary" variant="contained" disableElevation
                disabled={busy || loadingProp || !proposal || proposal.stale || !hasContent()}>
                {busy ? "Aplicando…" : isEdited() ? "Aprovar com ajuste" : "Aprovar e publicar"}
              </Button>
            </>
          ) : open ? (
            <Button onClick={() => setCorrStatus("pending")} disabled={busy}>Reabrir</Button>
          ) : null}
          <Button onClick={close} disabled={busy}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AgentCorrecoes;
